import { useEffect, useMemo, useRef, useState } from 'react'
import * as ChecklistHttp from '../../../../services/api/https/checklist-operacional/checklist.http'
import { errorHandler } from '../../../../services/util/error.service'
import {
  aplicarMudancaCampo,
  aplicarMudancaItem,
  atualizarCampo,
  atualizarEtapa,
  atualizarGrupo,
  atualizarItem,
  duplicarChecklist,
  duplicarItem,
  LIMITES,
  montarPayload,
  mover,
  novaEtapa,
  novoCampo,
  novoChecklist,
  novoGrupo,
  novoItem,
  parseChecklist,
  ultimoItem,
  ultimoItemDaEtapa,
  validarChecklist,
} from '../shared/checklist.util'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET: 'Carregando checklist',
  SAVE: 'Salvando checklist',
}

export function criarSnapshot(checklist) {
  return JSON.stringify(montarPayload(checklist))
}

function buildInitialState(tpId = 0) {
  const checklist = novoChecklist(tpId)

  return {
    checklist,
    snapshot: criarSnapshot(checklist),
    origem: null,
    loadingMessage: LOAD_MESSAGE.NONE,
    errorMessage: null,
    successMessage: null,
    showPreview: false,
    confirmVoltar: false,
    remocao: null,
    voltar: false,
    focoId: null,
  }
}

function inserirApos(lista, id, novo) {
  const indice = lista.findIndex(item => item.id === id)
  if (indice === -1) return [...lista, novo]

  return [...lista.slice(0, indice + 1), novo, ...lista.slice(indice + 1)]
}

export default function useEstruturaChecklist() {
  const [state, setState] = useState(() => buildInitialState())
  const montado = useRef(true)

  useEffect(() => {
    return () => {
      montado.current = false
    }
  }, [])

  const localDispatch = useMemo(() => {
    const alterarChecklist = (atualizar, extras = {}) =>
      setState(prev => ({ ...prev, ...extras, checklist: atualizar(prev.checklist) }))

    return {
      onInit(selectedTp, origem) {
        const { checklistId, duplicarDe } = origem || {}
        const id = checklistId || duplicarDe

        setState({
          ...buildInitialState(selectedTp),
          origem: id ? { id, duplicar: !!duplicarDe } : null,
          loadingMessage: id ? LOAD_MESSAGE.GET : LOAD_MESSAGE.NONE,
        })
      },
      async getChecklist(tpId, origem) {
        try {
          const doc = await ChecklistHttp.getChecklist(tpId, origem.id)
          if (!montado.current) return

          const checklist = origem.duplicar ? duplicarChecklist(doc) : parseChecklist(doc)

          setState(prev => ({
            ...prev,
            checklist,
            snapshot: origem.duplicar ? '' : criarSnapshot(checklist),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (err) {
          if (!montado.current) return

          setState(prev => ({
            ...prev,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(err),
            voltar: true,
          }))
        }
      },
      async save(tpId, checklist) {
        try {
          const saved = await ChecklistHttp.saveChecklist(tpId, montarPayload(checklist))
          if (!montado.current) return

          const salvo = parseChecklist(saved)

          setState(prev => ({
            ...prev,
            checklist: salvo,
            snapshot: criarSnapshot(salvo),
            origem: { id: salvo.id, duplicar: false },
            loadingMessage: LOAD_MESSAGE.NONE,
            successMessage: 'Checklist salvo com sucesso',
          }))
        } catch (err) {
          if (!montado.current) return

          setState(prev => ({
            ...prev,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(err),
          }))
        }
      },
      clearMessages() {
        setState(prev => ({ ...prev, errorMessage: null, successMessage: null }))
      },
      onBtnSalvarClicked() {
        setState(prev => {
          const erro = validarChecklist(prev.checklist)
          return erro
            ? { ...prev, errorMessage: erro }
            : { ...prev, loadingMessage: LOAD_MESSAGE.SAVE }
        })
      },
      onBtnVoltarClicked() {
        setState(prev => {
          const alterado = criarSnapshot(prev.checklist) !== prev.snapshot
          return alterado ? { ...prev, confirmVoltar: true } : { ...prev, voltar: true }
        })
      },
      onConfirmVoltar(confirmado) {
        setState(prev => ({ ...prev, confirmVoltar: false, voltar: confirmado }))
      },
      onTogglePreview(show) {
        setState(prev => ({ ...prev, showPreview: show }))
      },

      onIdentificacaoChange(chave, valor) {
        alterarChecklist(checklist => ({ ...checklist, [chave]: valor }))
      },
      onObservacoesChange(secao, chave, valor) {
        alterarChecklist(checklist => ({
          ...checklist,
          [secao]: { ...checklist[secao], [chave]: valor },
        }))
      },

      onAdicionarCampo(secao) {
        const campo = novoCampo()
        alterarChecklist(checklist => ({ ...checklist, [secao]: [...checklist[secao], campo] }), {
          focoId: campo.id,
        })
      },
      onCampoChange(secao, campoId, chave, valor) {
        alterarChecklist(checklist =>
          atualizarCampo(checklist, secao, campoId, campo =>
            aplicarMudancaCampo(campo, chave, valor),
          ),
        )
      },
      onRemoverCampo(secao, campoId) {
        alterarChecklist(checklist => ({
          ...checklist,
          [secao]: checklist[secao].filter(campo => campo.id !== campoId),
        }))
      },
      onMoverCampo(secao, campoId, direcao) {
        alterarChecklist(checklist => ({
          ...checklist,
          [secao]: mover(checklist[secao], campoId, direcao),
        }))
      },

      onAdicionarEtapa() {
        alterarChecklist(checklist => {
          const anterior = checklist.body[checklist.body.length - 1]
          return { ...checklist, body: [...checklist.body, novaEtapa(ultimoItemDaEtapa(anterior))] }
        })
      },
      onEtapaChange(etapaId, chave, valor) {
        alterarChecklist(checklist =>
          atualizarEtapa(checklist, etapaId, etapa => ({ ...etapa, [chave]: valor })),
        )
      },
      onMoverEtapa(etapaId, direcao) {
        alterarChecklist(checklist => ({
          ...checklist,
          body: mover(checklist.body, etapaId, direcao),
        }))
      },

      onAdicionarGrupo(etapaId) {
        alterarChecklist(checklist =>
          atualizarEtapa(checklist, etapaId, etapa => ({
            ...etapa,
            groups: [...etapa.groups, novoGrupo(ultimoItemDaEtapa(etapa))],
          })),
        )
      },
      onGrupoChange(etapaId, grupoId, chave, valor) {
        alterarChecklist(checklist =>
          atualizarGrupo(checklist, etapaId, grupoId, grupo => ({ ...grupo, [chave]: valor })),
        )
      },
      onMoverGrupo(etapaId, grupoId, direcao) {
        alterarChecklist(checklist =>
          atualizarEtapa(checklist, etapaId, etapa => ({
            ...etapa,
            groups: mover(etapa.groups, grupoId, direcao),
          })),
        )
      },

      onSolicitarRemocao(remocao) {
        setState(prev => ({ ...prev, remocao }))
      },
      onCancelarRemocao() {
        setState(prev => ({ ...prev, remocao: null }))
      },
      onConfirmarRemocao() {
        setState(prev => {
          if (!prev.remocao) return prev

          const { etapaId, grupoId } = prev.remocao
          const checklist = grupoId
            ? atualizarEtapa(prev.checklist, etapaId, etapa => ({
                ...etapa,
                groups: etapa.groups.filter(grupo => grupo.id !== grupoId),
              }))
            : { ...prev.checklist, body: prev.checklist.body.filter(e => e.id !== etapaId) }

          return { ...prev, checklist, remocao: null }
        })
      },

      onAdicionarItem(etapaId, grupoId, aposItemId) {
        setState(prev => {
          let item = null
          const checklist = atualizarGrupo(prev.checklist, etapaId, grupoId, grupo => {
            if (grupo.items.length >= LIMITES.itensPorGrupo) return grupo

            const base = grupo.items.find(i => i.id === aposItemId) || ultimoItem(grupo)
            item = novoItem(base)
            return { ...grupo, items: inserirApos(grupo.items, aposItemId, item) }
          })

          return { ...prev, checklist, focoId: item ? item.id : prev.focoId }
        })
      },
      onItemChange(etapaId, grupoId, itemId, chave, valor) {
        alterarChecklist(checklist =>
          atualizarItem(checklist, etapaId, grupoId, itemId, item =>
            aplicarMudancaItem(item, chave, valor),
          ),
        )
      },
      onDuplicarItem(etapaId, grupoId, itemId) {
        setState(prev => {
          let copia = null
          const checklist = atualizarGrupo(prev.checklist, etapaId, grupoId, grupo => {
            const original = grupo.items.find(i => i.id === itemId)
            if (!original || grupo.items.length >= LIMITES.itensPorGrupo) return grupo

            copia = duplicarItem(original)
            return { ...grupo, items: inserirApos(grupo.items, itemId, copia) }
          })

          return { ...prev, checklist, focoId: copia ? copia.id : prev.focoId }
        })
      },
      onRemoverItem(etapaId, grupoId, itemId) {
        alterarChecklist(checklist =>
          atualizarGrupo(checklist, etapaId, grupoId, grupo => ({
            ...grupo,
            items: grupo.items.filter(item => item.id !== itemId),
          })),
        )
      },
      onMoverItem(etapaId, grupoId, itemId, direcao) {
        alterarChecklist(checklist =>
          atualizarGrupo(checklist, etapaId, grupoId, grupo => ({
            ...grupo,
            items: mover(grupo.items, itemId, direcao),
          })),
        )
      },
    }
  }, [])

  return { state, localDispatch }
}
